import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Settings as SettingsIcon, KeyRound, Mail, UserCircle2, Users, Building2, Eye, EyeOff, ShieldAlert } from "lucide-react";
import { useEffect } from "react";
import { ModuleHeader } from "@/components/SaleCard";
import Settings, { MODULE_LABELS } from "@/pages/Settings";

const inputCls = "h-11 w-full rounded-lg border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none";

function Field({ label, children }) {
  return <label className="block"><span className="block text-xs font-semibold text-muted-foreground mb-1.5">{label}</span>{children}</label>;
}

function EmailCard() {
  const { user, refreshUser } = useAuth();
  const [f, setF] = useState({ email: "", pw: "" });
  const save = async () => {
    if (!f.email.trim() || !f.pw) return toast.error("Yeni e-posta ve mevcut şifre gerekli");
    try {
      await api.post("/auth/change-email", { new_email: f.email.trim(), current_password: f.pw });
      toast.success("E-posta adresiniz güncellendi");
      setF({ email: "", pw: "" });
      refreshUser().catch(() => {});
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="profile-email-card">
      <div className="flex items-center gap-2 mb-1"><Mail size={18} className="text-primary" /><h3 className="font-head font-semibold">E-posta Değiştir</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Mevcut: <b className="text-foreground" data-testid="profile-current-email">{user?.email}</b></p>
      <div className="space-y-3">
        <Field label="Yeni E-posta"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="profile-new-email" className={inputCls} /></Field>
        <Field label="Mevcut Şifre (doğrulama)"><input type="password" value={f.pw} onChange={(e) => setF({ ...f, pw: e.target.value })} data-testid="profile-email-password" className={inputCls} /></Field>
        <button onClick={save} data-testid="profile-email-save" className="h-11 w-full rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity">E-postayı Güncelle</button>
      </div>
    </div>
  );
}

function PasswordCard() {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const save = async () => {
    if (!f.current) return toast.error("Mevcut şifrenizi girin");
    if (f.next.length < 6) return toast.error("Yeni şifre en az 6 karakter olmalı");
    if (f.next !== f.confirm) return toast.error("Yeni şifreler eşleşmiyor");
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      toast.success("Şifreniz güncellendi");
      setF({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="profile-password-card">
      <div className="flex items-center gap-2 mb-4"><KeyRound size={18} className="text-primary" /><h3 className="font-head font-semibold">Şifre Değiştir</h3></div>
      <div className="space-y-3">
        <Field label="Mevcut Şifre"><input type="password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} data-testid="profile-pw-current" className={inputCls} /></Field>
        <Field label="Yeni Şifre (en az 6 karakter)"><input type="password" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} data-testid="profile-pw-new" className={inputCls} /></Field>
        <Field label="Yeni Şifre (Tekrar)"><input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="profile-pw-confirm" className={inputCls} /></Field>
        <button onClick={save} data-testid="profile-pw-save" className="h-11 w-full rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity">Şifreyi Güncelle</button>
      </div>
    </div>
  );
}

function Profile() {
  const { user } = useAuth();
  const mods = user?.role === "admin" ? Object.keys(MODULE_LABELS) : (user?.modules || ["kumes"]);
  return (
    <div data-testid="profile-settings">
      <div className="rounded-xl border border-border bg-card p-5 mb-4 flex items-center gap-4">
        <UserCircle2 size={40} className="text-primary shrink-0" />
        <div className="min-w-0">
          <div className="font-head font-semibold text-lg truncate">{user?.name || user?.email}</div>
          <div className="text-xs text-muted-foreground">{user?.role === "admin" ? "Yönetici (Admin)" : "Erişim"}: {mods.map((m) => MODULE_LABELS[m]).join(", ")}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <EmailCard />
        <PasswordCard />
      </div>
    </div>
  );
}

const SUPER_ADMIN_EMAIL = "koyuncubaki77@gmail.com";

function PasswordVault() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState({});
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users/passwords");
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="password-vault">
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 mb-4 flex items-start gap-2.5">
        <ShieldAlert size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-foreground">Bu bölüm yalnızca sizin hesabınıza özeldir. Bu değişiklikten <b>sonra</b> oluşturulan veya şifresi güncellenen kullanıcıların şifreleri görünür. Daha önce belirlenmiş şifreler güvenlik gereği okunamıyor — görmek için ilgili kullanıcının şifresini Kullanıcılar sekmesinden bir kez güncelleyin.</p>
      </div>
      {loading ? <div className="text-sm text-muted-foreground">Yükleniyor…</div> : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-4" data-testid={`pw-row-${r.email}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{r.name || r.email}</div>
                <div className="text-xs text-muted-foreground truncate">{r.email}</div>
              </div>
              <div className="flex items-center gap-2">
                {r.password ? (
                  <>
                    <code className="text-sm font-mono px-2.5 py-1 rounded bg-secondary" data-testid={`pw-value-${r.email}`}>{show[r.id] ? r.password : "••••••••"}</code>
                    <button onClick={() => setShow((s) => ({ ...s, [r.id]: !s[r.id] }))} data-testid={`pw-toggle-${r.email}`} className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-secondary hover:bg-secondary/70 transition-colors">
                      {show[r.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground italic">Okunamıyor — şifreyi güncelleyin</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ADMIN_TABS = [
  { k: "profile", label: "Hesap / Profilim", icon: UserCircle2 },
  { k: "system", label: "Kullanıcılar, Roller & Sistem", icon: Users },
];

export default function SettingsHub({ onBack }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSuper = (user?.email || "").trim().toLowerCase() === SUPER_ADMIN_EMAIL;
  const tabs = isSuper ? [...ADMIN_TABS, { k: "passwords", label: "Şifreler", icon: KeyRound }] : ADMIN_TABS;
  const [tab, setTab] = useState(isAdmin ? "system" : "profile");
  return (
    <div className="min-h-screen bg-background" data-testid="settings-hub">
      <ModuleHeader icon={SettingsIcon} title={isAdmin ? "Ayarlar" : "Hesap / Profil Ayarlarım"} onBack={onBack} testid="settings" />
      <div className={`p-4 mx-auto ${isAdmin ? "max-w-7xl" : "max-w-3xl"}`}>
        {isAdmin && (
          <div className="flex flex-wrap gap-2 mb-5">
            {tabs.map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`settings-tab-${t.k}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                <t.icon size={15} /> {t.label}
              </button>
            ))}
          </div>
        )}
        {isAdmin && tab === "passwords" && isSuper ? <PasswordVault /> : isAdmin && tab === "system" ? <Settings /> : <Profile />}
        {isAdmin && tab === "system" && <div className="mt-6 text-xs text-muted-foreground flex items-center gap-1.5"><Building2 size={13} /> Lokasyonlar, sürü, uyarı e-postası ve kullanıcı yönetimi sadece yöneticiye açıktır.</div>}
      </div>
    </div>
  );
}
